import {
  type ConnectionMethod,
  type ConnectorActionDetail,
  type ConnectorOperation,
  type ConnectorServiceActions,
  type InstalledPlugin,
  pluginContributionKey,
} from '@crowdex/plugin-contracts';
import { Switch } from '@crowdex/ui/components/switch';
import { FIELD_GEOMETRY, fieldClass } from '@crowdex/ui/lib/surfaces';
import { ChevronDown, KeyRound, LoaderCircle, PlugZap, Search, Server } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { nativePluginClient } from '@/lib/plugins/plugin-native-client';

/** How many actions a tab lists before "Show all". */
const SHOWN = 10;

function MethodIcon({ method }: { method: ConnectionMethod }) {
  if (method.kind === 'mcp') return <Server className="size-3.5" />;
  if (method.kind === 'oauth') return <PlugZap className="size-3.5" />;
  return <KeyRound className="size-3.5" />;
}

/** Text with its Markdown links (`[label](https://…)`) as links. */
function LinkedText({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  const pattern = /\[([^\]]+)\]\((https:\/\/[^)\s]+)\)/g;
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    const index = match.index ?? 0;
    parts.push(text.slice(last, index));
    parts.push(
      <a
        className="underline underline-offset-2 hover:text-foreground"
        href={match[2]}
        key={index}
        rel="noreferrer"
        target="_blank"
      >
        {match[1]}
      </a>,
    );
    last = index + match[0].length;
  }
  parts.push(text.slice(last));
  return <>{parts}</>;
}

type Parameter = { name: string; type: string; required: boolean; description: string };

/** The inputs an action's JSON schema declares, required ones first. */
export function actionParameters(schema: Record<string, unknown>): Parameter[] {
  const properties = (schema['properties'] ?? {}) as Record<string, Record<string, unknown>>;
  const required = new Set(
    Array.isArray(schema['required']) ? (schema['required'] as string[]) : [],
  );
  return Object.entries(properties)
    .map(([name, property]) => {
      const type = Array.isArray(property['enum'])
        ? (property['enum'] as unknown[]).map(String).join(' | ')
        : String(property['type'] ?? (property['anyOf'] ? 'any of several' : 'value'));
      return {
        name,
        type:
          type === 'array' && property['items']
            ? `array of ${String((property['items'] as { type?: unknown }).type ?? 'values')}`
            : type,
        required: required.has(name),
        description: String(property['description'] ?? property['title'] ?? ''),
      };
    })
    .sort((left, right) => Number(right.required) - Number(left.required));
}

/** What one action does and the inputs it takes, loaded when it is opened. */
function ActionDetail({ service, id }: { service: string; id: string }) {
  const [detail, setDetail] = useState<ConnectorActionDetail | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    nativePluginClient.connectorAction(service, id).then(
      (value) => !cancelled && setDetail(value),
      (reason: unknown) =>
        !cancelled && setError(reason instanceof Error ? reason.message : String(reason)),
    );
    return () => {
      cancelled = true;
    };
  }, [service, id]);
  if (error) return <p className="text-destructive text-xs">{error}</p>;
  if (detail === undefined)
    return (
      <p className="flex items-center gap-1.5 text-muted-foreground text-xs">
        <LoaderCircle className="size-3 animate-spin" /> Loading…
      </p>
    );
  if (detail === null)
    return <p className="text-muted-foreground text-xs">No details for this action.</p>;
  const parameters = actionParameters(detail.inputSchema);
  return (
    <div className="space-y-3">
      {detail.description && (
        <p className="whitespace-pre-line text-muted-foreground text-xs leading-5">
          <LinkedText text={detail.description} />
        </p>
      )}
      {parameters.length === 0 ? (
        <p className="text-muted-foreground text-xs">It takes no input.</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          {parameters.map((parameter) => (
            <div
              className="grid gap-x-3 border-border border-b px-3 py-2 last:border-b-0 sm:grid-cols-[minmax(0,12rem)_1fr]"
              key={parameter.name}
            >
              <span className="min-w-0 text-xs">
                <code className="break-all">{parameter.name}</code>
                {parameter.required && (
                  <span className="text-amber-600 dark:text-amber-300"> *</span>
                )}
                <span className="block text-muted-foreground">{parameter.type}</span>
              </span>
              <span className="text-muted-foreground text-xs leading-5">
                {parameter.description ? <LinkedText text={parameter.description} /> : null}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The catalog actions one way of connecting can run: those whose methods include it, or every
 * action when the index says all of the catalog's methods can.
 */
export function catalogMethodOperations(
  actions: ConnectorServiceActions,
  method: ConnectionMethod,
): ConnectorOperation[] {
  const index = actions.methods.indexOf(method);
  return actions.operations.filter((operation) => {
    const able = operation[3];
    return able === undefined || able.includes(index);
  });
}

/** Whether a way of connecting is used as it is, with no account. */
export function needsNoAccount(method: ConnectionMethod): boolean {
  return (
    method.kind === 'none' ||
    (method.kind === 'mcp' && !method.token && (method.signIn ?? 'none') === 'none')
  );
}

/** Why an MCP server's tools are not listed yet. */
function mcpToolsHint(method: ConnectionMethod): string {
  return needsNoAccount(method)
    ? 'The server lists its tools to agents when they use it.'
    : 'Connect an account with this way above to see the tools its server publishes.';
}

/**
 * What agents can do with a service, by way of connecting: one tab per method, each listing the
 * actions it runs (an MCP server's tools once an account is connected). With `plugin`, each
 * action can be turned off for that plugin.
 */
export function PluginAgentActions({
  service,
  plugin,
  busy,
  onToggle,
  methodHeader,
  allEnabled = true,
  onToggleAll,
  headingLevel = 3,
}: {
  service: string;
  plugin?: InstalledPlugin;
  busy: boolean;
  onToggle?: (plugin: InstalledPlugin, key: string, enabled: boolean) => void;
  /** What a tab shows above its actions: how to connect with that way. */
  methodHeader?: (method: ConnectionMethod) => ReactNode;
  /** Whether agents may use any of the service's actions; with `onToggleAll`, a switch for all. */
  allEnabled?: boolean;
  onToggleAll?: (enabled: boolean) => void;
  /** Its heading's level: under the page's section (h2), or under a service's own title (h3). */
  headingLevel?: 3 | 4;
}) {
  const Heading = headingLevel === 4 ? 'h4' : 'h3';
  const [actions, setActions] = useState<ConnectorServiceActions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [tools, setTools] = useState<Record<string, ConnectorOperation[] | 'failed'>>({});
  const [query, setQuery] = useState('');
  const [all, setAll] = useState(false);
  const [opened, setOpened] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    nativePluginClient.connectorService(service).then(
      (value) => !cancelled && setActions(value),
      (reason: unknown) =>
        !cancelled && setError(reason instanceof Error ? reason.message : String(reason)),
    );
    return () => {
      cancelled = true;
    };
  }, [service]);

  const methods = useMemo(
    () => (actions?.methods ?? []).filter((method) => method.kind !== 'none'),
    [actions],
  );
  const method = methods.find((candidate) => candidate.id === selected) ?? methods[0];

  // An MCP server lists its tools to a connected account only.
  useEffect(() => {
    if (method?.kind !== 'mcp' || tools[method.id] !== undefined) return;
    let cancelled = false;
    nativePluginClient.connectorTools(service, method.id).then(
      (value) => !cancelled && setTools((current) => ({ ...current, [method.id]: value })),
      () => !cancelled && setTools((current) => ({ ...current, [method.id]: 'failed' })),
    );
    return () => {
      cancelled = true;
    };
  }, [service, method, tools]);

  if (error) return <p className="text-destructive text-xs">{error}</p>;
  if (actions === null)
    return (
      <p className="flex items-center gap-1.5 text-muted-foreground text-xs">
        <LoaderCircle className="size-3 animate-spin" /> Loading actions…
      </p>
    );

  const listed: ConnectorOperation[] | 'failed' | undefined =
    method === undefined
      ? actions.operations
      : method.kind === 'mcp'
        ? tools[method.id]
        : catalogMethodOperations(actions, method);
  const operations = Array.isArray(listed) ? listed : [];
  const matching = operations.filter(([, title]) =>
    title.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()),
  );
  const shown = all ? matching : matching.slice(0, SHOWN);
  const disabled = new Set(plugin?.disabledContributions ?? []);

  return (
    <div className="space-y-3">
      {methods.length > 1 && (
        <div className="flex flex-wrap gap-1 rounded-xl border border-border p-1" role="tablist">
          {methods.map((candidate) => {
            const count =
              candidate.kind === 'mcp'
                ? (() => {
                    const loaded = tools[candidate.id];
                    return Array.isArray(loaded) && loaded.length > 0 ? loaded.length : null;
                  })()
                : catalogMethodOperations(actions, candidate).length;
            const active = candidate.id === method?.id;
            return (
              <button
                aria-selected={active}
                className={`type-ui flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs ${
                  active
                    ? 'bg-accent text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                key={candidate.id}
                onClick={() => {
                  setSelected(candidate.id);
                  setAll(false);
                }}
                role="tab"
                type="button"
              >
                <MethodIcon method={candidate} />
                {candidate.label}
                {count !== null && <span className="text-muted-foreground">{count}</span>}
              </button>
            );
          })}
        </div>
      )}
      {method && methodHeader ? methodHeader(method) : null}
      <div className="flex items-center justify-between gap-3 pt-1">
        <Heading className="type-caption text-muted-foreground">
          {method && methodHeader ? 'What agents can do with it' : 'What agents can do'}
          {operations.length > 0 && <span> · {operations.length}</span>}
        </Heading>
        <span className="flex items-center gap-3">
          {operations.length > SHOWN && (
            <label className="relative block w-56">
              <Search className="absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                aria-label="Search actions"
                className={fieldClass(FIELD_GEOMETRY, 'w-full pl-8')}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search actions"
                value={query}
              />
            </label>
          )}
          {onToggleAll && (
            <span className="flex items-center gap-2 text-muted-foreground text-xs">
              All
              <Switch
                aria-label={`${allEnabled ? 'Turn off' : 'Turn on'} every ${service} action`}
                checked={allEnabled}
                disabled={busy}
                onCheckedChange={onToggleAll}
              />
            </span>
          )}
        </span>
      </div>
      <div className="divide-y divide-border overflow-hidden rounded-panel border border-border">
        {method?.kind === 'mcp' && !Array.isArray(listed) ? (
          <p className="bg-background/55 px-4 py-3 text-muted-foreground text-xs">
            {listed === undefined ? (
              <span className="flex items-center gap-1.5">
                <LoaderCircle className="size-3 animate-spin" /> Listing the server's tools…
              </span>
            ) : (
              mcpToolsHint(method)
            )}
          </p>
        ) : shown.length === 0 ? (
          <p className="bg-background/55 px-4 py-3 text-muted-foreground text-xs">
            {operations.length === 0
              ? method?.kind === 'mcp'
                ? mcpToolsHint(method)
                : 'This way of connecting runs no action.'
              : 'No action matches.'}
          </p>
        ) : (
          shown.map(([id, title, kind]) => {
            const key = pluginContributionKey.action(service, id);
            // With every action off, each shows off and waits for "All" to come back on.
            const enabled = allEnabled && !disabled.has(key);
            const open = opened === id;
            return (
              <div className="bg-background/55" key={id}>
                <div className="flex items-center justify-between gap-4 px-3 py-1.5">
                  <button
                    aria-expanded={open}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    onClick={() => setOpened(open ? null : id)}
                    type="button"
                  >
                    <ChevronDown
                      className={`size-3.5 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
                    />
                    <span className="min-w-0 truncate text-sm">{title}</span>
                    {kind === 'trigger' && (
                      <span className="shrink-0 rounded-sm bg-accent px-1.5 text-caption text-muted-foreground">
                        Trigger
                      </span>
                    )}
                  </button>
                  {plugin && onToggle ? (
                    <Switch
                      aria-label={`${enabled ? 'Turn off' : 'Turn on'} ${title}`}
                      checked={enabled}
                      disabled={busy || !allEnabled}
                      onCheckedChange={(checked) => onToggle(plugin, key, checked)}
                    />
                  ) : null}
                </div>
                {open && (
                  <div className="px-3 pb-3 pl-9">
                    <ActionDetail service={service} id={id} />
                  </div>
                )}
              </div>
            );
          })
        )}
        {!all && matching.length > SHOWN && (
          <button
            className="type-ui block w-full bg-background/55 px-3 py-2 text-left text-primary text-xs hover:bg-accent"
            onClick={() => setAll(true)}
            type="button"
          >
            Show all {matching.length} actions
          </button>
        )}
      </div>
    </div>
  );
}
